<template>
  <div>
    <!-- Mobile sidebar toggle button -->
    <button class="mobile-toggle" @click="toggleSidebar" aria-label="Open Sidebar">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <line x1="3" y1="12" x2="21" y2="12"></line>
        <line x1="3" y1="6" x2="21" y2="6"></line>
        <line x1="3" y1="18" x2="21" y2="18"></line>
      </svg>
    </button>
    
    <aside class="sidebar" :class="{ 'open': isSidebarOpen, 'collapsed': isCollapsed && !isSidebarOpen, 'customizing': isCustomizing }">
      <SidebarHeader 
        :isCollapsed="isCollapsed" 
        @toggle-collapse="toggleCollapse" 
        @close-sidebar="toggleSidebar(false)" 
      />

      <div class="sidebar-scroll">
        <UserInfo 
          :isCollapsed="isCollapsed" 
          :userInitials="userInitials" 
        />

        <SearchBar 
          :isCollapsed="isCollapsed" 
          :searchQuery="searchQuery" 
          @update-search="updateSearch" 
          @clear-search="clearSearch" 
          @show-history="showSearchHistory = true"
          @hide-history="showSearchHistory = false"
          @search-focus="handleSearchFocus"
        />

        <div v-if="showSearchHistory && searchHistory.length > 0 && !isCollapsed && !searchActive" class="search-history">
          <div class="search-history-header">
            <h4>Recent Searches</h4>
            <button @click="clearSearchHistory" class="clear-history">Clear</button>
          </div>
          <div class="search-history-items">
            <button 
              v-for="(query, index) in searchHistory" 
              :key="index" 
              @click="selectSearchHistory(query)" 
              class="search-history-item"
            >
              {{ query }}
            </button>
          </div>
        </div>

        <div v-if="searchActive && !isCollapsed" class="search-results-header">
          <div class="search-stats">{{ filteredItemsCount }} results for "{{ searchQuery }}"</div>
        </div>

        <FavoritesSection 
          v-if="favorites.length > 0 && (!searchQuery || hasMatchingFavorites)"
          :favorites="filteredFavorites" 
          @remove-favorite="removeFavorite" 
        />

        <RecentPagesSection 
          v-if="recentPages.length > 0 && (!searchQuery || hasMatchingRecents)"
          :recentPages="filteredRecentPages" 
        />

        <div class="customize-bar" v-if="!isCollapsed && !searchActive">
          <button @click="toggleCustomizing" class="customize-button">
            {{ isCustomizing ? 'Save Layout' : 'Customize Sidebar' }}
          </button>
        </div>

        <SidebarNavigation 
          :isCollapsed="isCollapsed"
          :sortedGroups="sortedGroups"
          :expandedGroups="expandedGroups"
          :searchQuery="searchQuery"
          :isCustomizing="isCustomizing"
          :organizedMenuItems="organizedMenuItems"
          :loading="loading"
          :error="error"
          :searchActive="searchActive"
          @toggle-group="toggleGroup"
          @show-quick-actions="showQuickActions"
          @start-drag="startDrag"
          @drop="onDrop"
          @retry-fetch="fetchSidebarItems"
        />
      </div>

      <SidebarFooter 
        v-if="!searchActive"
        :isCollapsed="isCollapsed" 
        @logout="logout" 
      />
      
      <ThemeSwitcher 
        :isCollapsed="isCollapsed" 
        :currentTheme="currentTheme" 
        @toggle-theme="toggleTheme" 
      />
    </aside>

    <!-- Quick Actions Menu (Context Menu) -->
    <div v-if="showingQuickActions" class="quick-actions" :style="quickActionsStyle">
      <button @click="addToFavorites(activeMenuItem)" class="quick-action">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
        </svg>
        Add to Favorites
      </button>
      <button @click="pinToTop(activeMenuItem)" class="quick-action">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 2L12 22M12 2L8 6M12 2L16 6"></path>
        </svg>
        Pin to Top
      </button>
      <button @click="copyLink(activeMenuItem)" class="quick-action">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
          <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
        </svg>
        Copy Link
      </button>
      <button @click="hideQuickActions" class="quick-action quick-action-close">Close</button>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import apiService from '@/services/api';
import SidebarHeader from './sidebar/SidebarHeader.vue';
import UserInfo from './sidebar/UserInfo.vue';
import SearchBar from './sidebar/SearchBar.vue';
import FavoritesSection from './sidebar/FavoritesSection.vue';
import RecentPagesSection from './sidebar/RecentPagesSection.vue';
import SidebarNavigation from './sidebar/Navigation.vue';
import ThemeSwitcher from './sidebar/ThemeSwitcher.vue';
import SidebarFooter from './sidebar/SidebarFooter.vue';

export default {
  name: 'AppSidebar',
  components: {
    SidebarHeader,
    UserInfo,
    SearchBar,
    FavoritesSection,
    RecentPagesSection,
    SidebarNavigation,
    ThemeSwitcher,
    SidebarFooter
  },
  setup(props, { emit }) {
    const route = useRoute();
    
    // Sidebar State
    const isCollapsed = ref(false); // Always set to false to keep sidebar open
    const isSidebarOpen = ref(false);
    const isCustomizing = ref(false);
    const currentTheme = ref(localStorage.getItem('theme') || 'dark');
    
    // Search
    const searchQuery = ref('');
    const searchHistory = ref(JSON.parse(localStorage.getItem('searchHistory') || '[]'));
    const showSearchHistory = ref(false);
    const searchActive = ref(false);
    
    // Sidebar Menu
    const sidebarItems = ref([]);
    const loading = ref(false);
    const error = ref(null);
    
    // Navigation Groups
    const groupOrder = ref(JSON.parse(localStorage.getItem('sidebarGroupOrder') || 
      '["Dashboard & Analytics", "Course Management", "Course Creation", "Content Management", "Homepage & Marketing", "User Management", "Media Library", "Assessment Tools", "Settings & Tools"]'
    ));
    
    const expandedGroups = ref(JSON.parse(localStorage.getItem('expandedGroups') || '{}'));
    
    // Initialize default expanded groups if none saved
    if (Object.keys(expandedGroups.value).length === 0) {
      groupOrder.value.forEach(group => {
        expandedGroups.value[group] = group === 'Dashboard & Analytics';
      });
    }
    
    // User data
    const userInitials = ref('AU'); // Could be fetched from user profile
    
    // Favorites
    const favorites = ref(JSON.parse(localStorage.getItem('favorites') || '[]'));
    
    // Recent Pages
    const recentPages = ref(JSON.parse(localStorage.getItem('recentPages') || '[]'));
    
    // Context menu for quick actions
    const showingQuickActions = ref(false);
    const quickActionsPosition = ref({ x: 0, y: 0 });
    const activeMenuItem = ref(null);
    
    // Modified search history limit
    const MAX_SEARCH_HISTORY = 10; // Increased from 5
    
    // Modified recent pages limit
    const MAX_RECENT_PAGES = 8; // Increased from 5
    
    // Fetch sidebar items from API
    const fetchSidebarItems = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          error.value = 'Authentication token not found';
          loading.value = false;
          return;
        }
        
        const response = await axios.get('https://api.kewaar.com/api/admin/sidebar-items', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });
        
        if (response.data && response.data.success) {
          sidebarItems.value = response.data.data;
          processMenuItems();
        } else {
          error.value = 'Failed to load menu items';
        }
      } catch (err) {
        console.error('Error fetching sidebar items:', err);
        error.value = err.message || 'Failed to load menu items';
      } finally {
        loading.value = false;
      }
    };
    
    // Process menu items to create hierarchical structure
    const processMenuItems = () => {
      // Group items by section
      const sectionMap = {};
      
      // Create mapping of header items for easier lookup
      const headerItems = {};
      sidebarItems.value.forEach(item => {
        if (item.is_header) {
          headerItems[item.id] = item;
          
          // Create section if it doesn't exist
          if (!sectionMap[item.section]) {
            sectionMap[item.section] = [];
          }
          
          // Add header to its section
          sectionMap[item.section].push({
            ...item,
            children: []
          });
        }
      });
      
      // Add children to their parent headers
      sidebarItems.value.forEach(item => {
        if (!item.is_header && item.parent_id && headerItems[item.parent_id]) {
          const parentHeader = headerItems[item.parent_id];
          const parentSection = sectionMap[parentHeader.section];
          
          if (parentSection) {
            const parent = parentSection.find(h => h.id === item.parent_id);
            if (parent) {
              parent.children.push(item);
            }
          }
        }
      });
      
      // Sort each section by order
      Object.keys(sectionMap).forEach(section => {
        sectionMap[section].sort((a, b) => a.order - b.order);
        
        // Sort children by order
        sectionMap[section].forEach(header => {
          if (header.children && header.children.length) {
            header.children.sort((a, b) => a.order - b.order);
          }
        });
      });
      
      // Update the group order based on the sections
      groupOrder.value = Object.keys(sectionMap);
      
      // Initialize expanded groups if not already set
      groupOrder.value.forEach(group => {
        if (expandedGroups.value[group] === undefined) {
          expandedGroups.value[group] = group === 'Dashboard';
        }
      });
      
      localStorage.setItem('sidebarGroupOrder', JSON.stringify(groupOrder.value));
    };
    
    // Computed properties
    const sortedGroups = computed(() => {
      return groupOrder.value;
    });
    
    const quickActionsStyle = computed(() => {
      return {
        top: `${quickActionsPosition.value.y}px`,
        left: `${quickActionsPosition.value.x}px`
      };
    });
    
    const organizedMenuItems = computed(() => {
      const result = {};
      
      // First pass: get header items
      sidebarItems.value.forEach(item => {
        if (item.is_header) {
          if (!result[item.section]) {
            result[item.section] = {};
          }
          result[item.section][item.id] = {
            ...item,
            children: []
          };
        }
      });
      
      // Second pass: add children to headers
      sidebarItems.value.forEach(item => {
        if (!item.is_header && item.parent_id) {
          // Find the parent header's section
          for (const section in result) {
            if (result[section][item.parent_id]) {
              result[section][item.parent_id].children.push(item);
              break;
            }
          }
        }
      });
      
      // Sort headers and children by order
      for (const section in result) {
        for (const headerId in result[section]) {
          result[section][headerId].children.sort((a, b) => a.order - b.order);
        }
        
        // Convert object to array and sort by order
        result[section] = Object.values(result[section]).sort((a, b) => a.order - b.order);
      }
      
      return result;
    });
    
    // Enhanced fuzzy search with scoring
    const fuzzyMatch = (text, query) => {
      if (!query) return { match: true, score: 0 };
      if (!text) return { match: false, score: 0 };
      
      const searchLower = query.toLowerCase();
      const textLower = text.toLowerCase();
      
      // Direct substring match gets highest score
      if (textLower.includes(searchLower)) {
        return { match: true, score: 1 };
      }
      
      // Fuzzy match with scoring
      let searchIndex = 0;
      let score = 0;
      let lastMatchIndex = -1;
      
      for (let i = 0; i < textLower.length && searchIndex < searchLower.length; i++) {
        if (textLower[i] === searchLower[searchIndex]) {
          // Bonus for consecutive matches
          if (lastMatchIndex === i - 1) {
            score += 0.2;
          }
          score += 0.1;
          lastMatchIndex = i;
          searchIndex++;
        }
      }
      
      return {
        match: searchIndex === searchLower.length,
        score: score
      };
    };
    
    // Enhanced search filtering with scoring
    const filteredFavorites = computed(() => {
      if (!searchQuery.value) return favorites.value;
      
      return favorites.value
        .map(item => ({
          ...item,
          searchScore: Math.max(
            fuzzyMatch(item.title, searchQuery.value).score,
            fuzzyMatch(item.path, searchQuery.value).score
          )
        }))
        .filter(item => item.searchScore > 0)
        .sort((a, b) => b.searchScore - a.searchScore);
    });
    
    const hasMatchingFavorites = computed(() => {
      return filteredFavorites.value.length > 0;
    });
    
    const filteredRecentPages = computed(() => {
      if (!searchQuery.value) return recentPages.value;
      
      return recentPages.value.filter(item => 
        fuzzyMatch(item.title, searchQuery.value).match ||
        fuzzyMatch(item.path, searchQuery.value).match
      );
    });
    
    const hasMatchingRecents = computed(() => {
      return filteredRecentPages.value.length > 0;
    });
    
    // Count of all items matching search query
    const filteredItemsCount = computed(() => {
      if (!searchQuery.value) return 0;
      
      let count = filteredFavorites.value.length + filteredRecentPages.value.length;
      
      // Count matching items in regular menu
      for (const section in organizedMenuItems.value) {
        organizedMenuItems.value[section].forEach(header => {
          // Check header
          if (fuzzyMatch(header.title, searchQuery.value).match) {
            count++;
          }
          
          // Check children
          header.children.forEach(item => {
            if (fuzzyMatch(item.title, searchQuery.value).match || 
                fuzzyMatch(item.section, searchQuery.value).match ||
                fuzzyMatch(item.url, searchQuery.value).match) {
              count++;
            }
          });
        });
      }
      
      return count;
    });
    
    // Methods
    const toggleCollapse = () => {
      // Always maintain sidebar open state
      isCollapsed.value = false;
      localStorage.setItem('sidebarCollapsed', 'false');
      emit('sidebar-collapse', isCollapsed.value);
    };
    
    const toggleSidebar = (value) => {
      isSidebarOpen.value = typeof value === 'boolean' ? value : !isSidebarOpen.value;
      
      if (isSidebarOpen.value) {
        document.body.classList.add('sidebar-open');
      } else {
        document.body.classList.remove('sidebar-open');
      }
    };
    
    const toggleTheme = () => {
      currentTheme.value = currentTheme.value === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', currentTheme.value);
      localStorage.setItem('theme', currentTheme.value);
    };
    
    const toggleGroup = (groupName) => {
      expandedGroups.value[groupName] = !expandedGroups.value[groupName];
      localStorage.setItem('expandedGroups', JSON.stringify(expandedGroups.value));
    };
    
    const toggleCustomizing = () => {
      isCustomizing.value = !isCustomizing.value;
      if (!isCustomizing.value) {
        localStorage.setItem('sidebarGroupOrder', JSON.stringify(groupOrder.value));
      }
    };
    
    const updateSearch = (query) => {
      searchQuery.value = query;
      searchActive.value = !!query;
      
      // Hide search history when actively searching
      if (searchActive.value) {
        showSearchHistory.value = false;
        
        // Auto-expand all groups when searching
        groupOrder.value.forEach(group => {
          expandedGroups.value[group] = true;
        });
      }
    };
    
    const clearSearch = () => {
      if (searchQuery.value) {
        // Add to search history if not empty and not already the last item
        if (searchQuery.value.trim() && 
            (searchHistory.value.length === 0 || 
             searchHistory.value[searchHistory.value.length - 1] !== searchQuery.value)) {
          searchHistory.value.push(searchQuery.value);
          // Keep only last 5 searches
          if (searchHistory.value.length > 5) {
            searchHistory.value.shift();
          }
          localStorage.setItem('searchHistory', JSON.stringify(searchHistory.value));
        }
        searchQuery.value = '';
        searchActive.value = false;
      }
    };
    
    const selectSearchHistory = (query) => {
      searchQuery.value = query;
      searchActive.value = true;
      showSearchHistory.value = false;
      
      // Auto-expand all groups when searching
      groupOrder.value.forEach(group => {
        expandedGroups.value[group] = true;
      });
    };
    
    const clearSearchHistory = () => {
      searchHistory.value = [];
      localStorage.setItem('searchHistory', JSON.stringify(searchHistory.value));
      showSearchHistory.value = false;
    };
    
    const handleSearchFocus = () => {
      // When search is focused, show history if there's history and no query
      if (searchHistory.value.length > 0 && !searchQuery.value) {
        showSearchHistory.value = true;
      } else if (searchQuery.value) {
        // If there's already a query (search is active), don't show history
        showSearchHistory.value = false;
      }
    };
    
    const showQuickActions = (item, event) => {
      event.preventDefault();
      activeMenuItem.value = item;
      quickActionsPosition.value = {
        x: event.clientX,
        y: event.clientY
      };
      showingQuickActions.value = true;
      
      // Close quick actions when clicking outside
      document.addEventListener('click', hideQuickActions);
    };
    
    const hideQuickActions = () => {
      showingQuickActions.value = false;
      document.removeEventListener('click', hideQuickActions);
    };
    
    const addToFavorites = (item) => {
      if (!favorites.value.some(f => f.path === item.path)) {
        favorites.value.push(item);
        localStorage.setItem('favorites', JSON.stringify(favorites.value));
      }
      hideQuickActions();
    };
    
    const removeFavorite = (item) => {
      favorites.value = favorites.value.filter(f => f.path !== item.path);
      localStorage.setItem('favorites', JSON.stringify(favorites.value));
    };
    
    const pinToTop = (item) => {
      // Remove from current position
      favorites.value = favorites.value.filter(f => f.path !== item.path);
      // Add to beginning
      favorites.value.unshift(item);
      localStorage.setItem('favorites', JSON.stringify(favorites.value));
      hideQuickActions();
    };
    
    const copyLink = (item) => {
      const url = window.location.origin + item.path;
      navigator.clipboard.writeText(url).then(() => {
        // You could add a toast notification here
        console.log('Link copied to clipboard');
      });
      hideQuickActions();
    };
    
    const startDrag = (event, index) => {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.dropEffect = 'move';
      event.dataTransfer.setData('text/plain', index);
      
      // Add visual feedback
      event.target.classList.add('dragging');
    };
    
    const onDrop = (event, targetIndex) => {
      const sourceIndex = event.dataTransfer.getData('text/plain');
      if (sourceIndex !== targetIndex) {
        const groups = [...groupOrder.value];
        const [movedGroup] = groups.splice(sourceIndex, 1);
        groups.splice(targetIndex, 0, movedGroup);
        groupOrder.value = groups;
        
        // Save to localStorage
        localStorage.setItem('sidebarGroupOrder', JSON.stringify(groups));
        
        // Remove visual feedback
        event.target.classList.remove('dragging');
      }
    };
    
    const logout = async () => {
      try {
        // Use the apiService for logout
        await apiService.auth.logout();
        
        // Clear token and redirect to login page
        localStorage.removeItem('token');
        window.location.href = '/';
      } catch (error) {
        console.error('Logout failed:', error);
        // Still clear token and redirect even if API call fails
        localStorage.removeItem('token');
        window.location.href = '/';
      }
    };
    
    // Helper function to convert API items to menu format
    const getMenuItemFromApiItem = (apiItem) => {
      return {
        title: apiItem.title,
        group: apiItem.section || 'Other',
        path: apiItem.url || '#',
        icon: apiItem.icon || 'circle',
        roles: apiItem.roles.map(r => r.name),
        permissions: apiItem.permissions.map(p => p.name)
      };
    };
    
    // Enhanced keyboard shortcuts
    const setupKeyboardShortcuts = () => {
      const handleKeydown = (event) => {
        // Existing shortcuts
        if ((event.ctrlKey && event.key === 'f') || event.key === '/') {
          const searchInput = document.querySelector('.sidebar .search-input');
          if (searchInput) {
            event.preventDefault();
            searchInput.focus();
          }
        }
        
        if (event.key === 'Escape' && searchActive.value) {
          clearSearch();
          event.preventDefault();
        }
        
        // New shortcuts
        if (event.ctrlKey && event.key === 'b') {
          event.preventDefault();
          toggleCollapse();
        }
        
        if (event.ctrlKey && event.key === 'h') {
          event.preventDefault();
          toggleSidebar();
        }
        
        if (event.ctrlKey && event.key === 'm') {
          event.preventDefault();
          toggleCustomizing();
        }
      };
      
      document.addEventListener('keydown', handleKeydown);
      
      onBeforeUnmount(() => {
        document.removeEventListener('keydown', handleKeydown);
      });
    };
    
    // Track page visits for recent pages
    watch(() => route.fullPath, (newPath) => {
      if (!newPath) return;
      
      // Find menu item that matches the current path
      let currentPage = null;
      
      for (const item of sidebarItems.value) {
        if (item.url === newPath && !item.is_header) {
          currentPage = getMenuItemFromApiItem(item);
          break;
        }
      }
      
      if (currentPage) {
        // Remove if already in recent pages
        recentPages.value = recentPages.value.filter(page => page.path !== currentPage.path);
        
        // Add to the beginning
        recentPages.value.unshift(currentPage);
        
        // Keep only last 5 pages
        if (recentPages.value.length > 5) {
          recentPages.value.pop();
        }
        
        localStorage.setItem('recentPages', JSON.stringify(recentPages.value));
      }
    }, { immediate: true });
    
    // Initialize theme
    onMounted(() => {
      document.documentElement.setAttribute('data-theme', currentTheme.value);
      
      // Always ensure sidebar is open (not collapsed)
      isCollapsed.value = false;
      localStorage.setItem('sidebarCollapsed', 'false');
      
      // Fetch menu data
      fetchSidebarItems();
      
      // Setup keyboard shortcuts
      setupKeyboardShortcuts();
      
      // Close sidebar when clicking outside on mobile
      const handleClickOutside = (event) => {
        if (isSidebarOpen.value && !event.target.closest('.sidebar') && !event.target.closest('.mobile-toggle')) {
          toggleSidebar(false);
        }
      };
      
      document.addEventListener('click', handleClickOutside);
      
      onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside);
      });
    });
    
    return {
      // State
      isCollapsed,
      isSidebarOpen,
      isCustomizing,
      currentTheme,
      searchQuery,
      searchHistory,
      showSearchHistory,
      searchActive,
      favorites,
      recentPages,
      expandedGroups,
      sortedGroups,
      userInitials,
      showingQuickActions,
      quickActionsStyle,
      activeMenuItem,
      sidebarItems,
      organizedMenuItems,
      loading,
      error,
      
      // Computed
      filteredFavorites,
      filteredRecentPages,
      filteredItemsCount,
      hasMatchingFavorites,
      hasMatchingRecents,
      
      // Methods
      toggleCollapse,
      toggleSidebar,
      toggleTheme,
      toggleGroup,
      toggleCustomizing,
      updateSearch,
      clearSearch,
      selectSearchHistory,
      clearSearchHistory,
      handleSearchFocus,
      showQuickActions,
      hideQuickActions,
      addToFavorites,
      removeFavorite,
      startDrag,
      onDrop,
      logout,
      fetchSidebarItems,
      pinToTop,
      copyLink,
      MAX_SEARCH_HISTORY,
      MAX_RECENT_PAGES
    };
  }
};
</script>

<style scoped>
.sidebar {
  position: fixed;
  height: 100vh;
  width: 300px;
  background: #0f172a;
  color: white;
  display: flex;
  flex-direction: column;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  left: 0;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-scroll {
  flex: 1;
  padding: 1.25rem 1.25rem 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  background-color: #0f172a;
}

.sidebar-scroll::-webkit-scrollbar {
  width: 4px;
}

.sidebar-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-scroll::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
}

.logo {
  font-size: 2rem;
  font-weight: 800;
  margin: 0;
  color: var(--sidebar-logo-color, #fff);
  letter-spacing: 0.75px;
}

.mini-logo {
  font-size: 1.75rem;
}

.mobile-toggle {
  display: none;
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 990;
  background: rgba(17, 24, 39, 0.9);
  color: white;
  border: none;
  border-radius: 8px;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.mobile-toggle svg {
  width: 24px;
  height: 24px;
}

.customize-bar {
  margin: 1.5rem 0;
  padding: 0.5rem 0;
  display: flex;
  justify-content: center;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.customize-button {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.customize-button:hover {
  background: rgba(255, 255, 255, 0.1);
}

.customizing .customize-button {
  background: rgba(79, 70, 229, 0.15);
  color: #4f46e5;
  border-color: rgba(79, 70, 229, 0.3);
}

.search-history {
  background: #1f2937;
  border-radius: 8px;
  margin-top: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.search-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.search-history-header h4 {
  font-size: 0.8rem;
  margin: 0;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.7);
}

.clear-history {
  background: none;
  border: none;
  color: #4f46e5;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
}

.search-history-items {
  padding: 0.5rem 0;
}

.search-history-item {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.search-history-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.search-results-header {
  margin: 0.5rem 0 1rem;
  padding: 0.5rem 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
}

.search-stats {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
}

.quick-actions {
  position: fixed;
  background: #1f2937;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  z-index: 1010;
  padding: 0.5rem 0;
  min-width: 180px;
}

.quick-action {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  color: white;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-action:hover {
  background: rgba(255, 255, 255, 0.1);
}

.quick-action svg {
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.2s ease;
}

.quick-action:hover svg {
  color: white;
}

.quick-action-close {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
}

/* Drag and drop styles */
.dragging {
  opacity: 0.5;
  background: rgba(255, 255, 255, 0.1);
}

/* Light theme overrides for quick actions */
:root[data-theme="light"] .quick-action {
  color: #1e293b;
}

:root[data-theme="light"] .quick-action:hover {
  background: rgba(15, 23, 42, 0.05);
}

:root[data-theme="light"] .quick-action svg {
  color: rgba(15, 23, 42, 0.6);
}

:root[data-theme="light"] .quick-action:hover svg {
  color: #0f172a;
}

/* Ensure sidebar maintains dark background in light mode */
:root[data-theme="light"] .sidebar {
  background: #0f172a;
  color: white;
}

/* Mobile styles */
@media (max-width: 768px) {
  .sidebar {
    left: -300px;
  }
  
  .sidebar.open {
    left: 0;
  }
  
  .mobile-toggle {
    display: flex;
    background: rgba(15, 23, 42, 0.95);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  body.sidebar-open::after {
    background: rgba(15, 23, 42, 0.6);
  }
}
</style> 